/**
 * Converts a hex color string to HSL (Hue, Saturation, Lightness) values
 * Reference: W3C CSS Color Module Level 4
 * https://www.w3.org/TR/css-color-4/#rgb-to-hsl
 *
 * The conversion process:
 * 1. Convert hex to RGB (base 16 to decimal, normalized to [0,1])
 * 2. Find the minimum and maximum RGB values to determine lightness
 * 3. Calculate saturation based on lightness
 * 4. Calculate hue based on which RGB component is the maximum
 */
function hexToHSL(hexProp: string) {
	let hex = hexProp.replace(/^#/, '');
	if (hex.length === 3) {
		hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
	}
	const r = parseInt(hex.slice(0, 2), 16) / 255;
	const g = parseInt(hex.slice(2, 4), 16) / 255;
	const b = parseInt(hex.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h;
	let s;
	const l = (max + min) / 2;

	if (max === min) {
		h = 0;
		s = 0;
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
			default:
				h = (r - g) / d + 4;
				break;
		}
		h *= 60;
	}

	return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}
export function getAdjustedColor({
	dominantColor,
	isDarkMode,
}: {
	dominantColor: string;
	isDarkMode: boolean;
}): string {
	// Convert the hex to HSL
	const { h, s, l } = hexToHSL(dominantColor);

	if (isDarkMode) {
		// Dont need much adjustment for dark mode - just darken it
		return `${h} ${s}% ${Math.min(l, 26)}%`;
	}

	const LIGHTNESS_TARGET = 75;
	const HIGH_LIGHTNESS_THRESHOLD = 70;

	if (l > HIGH_LIGHTNESS_THRESHOLD || (l > 53 && s > 60)) {
		// Text already readable
		return `${h} ${s}% ${Math.max(l, 64)}%`;
	}

	let saturation = s;

	// Cap over-saturated blues
	if (h >= 200 && h <= 220 && s > 65) {
		saturation = 65;
	}

	return `${h} ${saturation}% ${LIGHTNESS_TARGET}%`;
}
